import {getTestProps} from '@benchling/js-test-utils';
import {Badge, Button, createStyles, Group, Paper, Stack, Text} from '@mantine/core';
import React, {useState} from 'react';

import * as NavConstants from 'constants/NavConstants';
import canOpenFile from 'file-browser/canOpenFile';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {setAgentFoundryHumanActionItemDone} from 'llm/chat-library/foundries/AgentFoundriesDAO';
import {getActionItemCategoryColor} from 'llm/chat-library/foundries/AgentFoundryActionItemCategoryPalette';
import {
  openExistingChat,
  openRoutineHomepageView,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import ButtonWithDisabledReason from 'ui-library/buttons/ButtonWithDisabledReason';
import APIUtil from 'util/APIUtil';
import {getUserMessage} from 'util/Net';

import {AgentFoundryQuery_agentFoundry_AgentFoundryNode_humanActionItems_AgentFoundryHumanActionItemNode as HumanActionItem} from './__generated/AgentFoundryQuery';

interface AgentFoundryNeedsAttentionProps {
  agentFoundryId: string;
  /** Action items in the order the Coordinator ranked them, most important first. */
  actionItems: Array<HumanActionItem>;
  currentUserId: string | null;
  /** Called after an action item is marked done or not done, so the parent can reload the action items. */
  onActionItemDoneChanged: () => Promise<unknown>;
}

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.white,
  },
  doneCard: {
    opacity: 0.6,
  },
  doneTitle: {
    color: theme.colors.gray[6],
    textDecoration: 'line-through',
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  actions: {
    flexShrink: 0,
  },
  assignee: {
    color: theme.colors.gray[7],
  },
}));

/** The number of action items the default view of the Needs attention tab asks a human to act on. */
export function countOpenHumanActionItems(actionItems: Array<HumanActionItem>): number {
  return actionItems.filter((actionItem) => !actionItem.isDone).length;
}

/** Opens the chat in the semi-expanded sidepanel, so the foundry page stays visible beside it. */
function openChatInSidepanel(chatId: string): void {
  openExistingChat(chatId);
  SidepanelActions.unexpandToSection(NavConstants.SECTIONS.AI);
}

/** Opens the agent's homepage in the semi-expanded sidepanel, so the foundry page stays visible beside it. */
function openAgentInSidepanel(agentId: string): void {
  openRoutineHomepageView(agentId);
  SidepanelActions.unexpandToSection(NavConstants.SECTIONS.AI);
}

/**
 * The Needs attention tab of an Agent Foundry: a short, priority-ordered list of action items the
 * Coordinator wants a human to do, shown in the order stored (never re-sorted). Each action item is a
 * self-contained card with its title, description, category pill and assignee, a Review button that opens
 * the Benchling object the action item is about (its context item) -- a chat session or agent opens
 * beside the foundry page, any other object opens the way it does elsewhere in the app -- and a button to
 * mark it complete. An action item with no context item has no Review button. Done action items stay in
 * place, grayed out.
 */
export default function AgentFoundryNeedsAttention({
  agentFoundryId,
  actionItems,
  currentUserId,
  onActionItemDoneChanged,
}: AgentFoundryNeedsAttentionProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [pendingActionItemId, setPendingActionItemId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleDoneChange = async (actionItem: HumanActionItem, isDone: boolean): Promise<void> => {
    setPendingActionItemId(actionItem.itemId);
    setErrorMessage(null);
    try {
      await setAgentFoundryHumanActionItemDone(agentFoundryId, actionItem.itemId, isDone);
      await onActionItemDoneChanged();
    } catch (e) {
      const userMessage = getUserMessage(e);
      if (userMessage == null) {
        throw e;
      }
      setErrorMessage(userMessage);
    } finally {
      setPendingActionItemId(null);
    }
  };

  return (
    <Stack spacing="sm">
      {errorMessage != null && <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />}
      {actionItems.map((actionItem, index) => {
        const categoryColor =
          actionItem.category == null ? null : getActionItemCategoryColor(actionItem.category);
        const {assignee} = actionItem;
        return (
          <Paper
            key={actionItem.itemId}
            withBorder={true}
            radius="md"
            p="md"
            className={cx(classes.card, {[classes.doneCard]: actionItem.isDone})}
            {...getTestProps({
              component: 'AgentFoundryNeedsAttention',
              element: 'item',
              key: actionItem.itemId,
            })}
            data-amp-safe={false}
          >
            <Group position="apart" align="flex-start" noWrap={true} spacing="md">
              <Stack spacing={6} className={classes.content}>
                <Text size="sm" fw={600} className={cx({[classes.doneTitle]: actionItem.isDone})}>
                  {actionItem.title}
                </Text>
                {actionItem.description !== '' && (
                  <Text size="xs" c="dimmed">
                    {actionItem.description}
                  </Text>
                )}
                {(categoryColor != null || assignee != null) && (
                  <Group spacing="sm">
                    {categoryColor != null && (
                      <Badge
                        size="sm"
                        radius="xl"
                        styles={{
                          root: {
                            backgroundColor: categoryColor.backgroundColor,
                            color: categoryColor.textColor,
                            fontWeight: 500,
                          },
                          // Mantine's Badge uppercases its label on the inner element.
                          inner: {textTransform: 'none'},
                        }}
                      >
                        {actionItem.category}
                      </Badge>
                    )}
                    {assignee != null && (
                      <Group spacing={6} noWrap={true} className={classes.assignee}>
                        <Avatar entity={assignee} size={AvatarSize.XSMALL} />
                        <Text size="xs">
                          {assignee.name}
                          {currentUserId != null && assignee.id === currentUserId ? ' (you)' : ''}
                        </Text>
                      </Group>
                    )}
                  </Group>
                )}
              </Stack>
              <Group spacing="xs" noWrap={true} className={classes.actions}>
                <ReviewButton actionItem={actionItem} index={index} />
                <Button
                  {...getTestProps({
                    component: 'AgentFoundryNeedsAttention',
                    element: 'markCompleteButton',
                    index: String(index),
                  })}
                  variant="secondary"
                  size="xs"
                  disabled={pendingActionItemId === actionItem.itemId}
                  aria-label={`${actionItem.isDone ? 'Undo mark complete' : 'Mark complete'}: "${actionItem.title}"`}
                  onClick={() => {
                    handleDoneChange(actionItem, !actionItem.isDone);
                  }}
                >
                  {actionItem.isDone ? 'Undo mark complete' : 'Mark complete'}
                </Button>
              </Group>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}

/**
 * The Review button of an action item: opens its context item, or renders nothing when there is none or
 * the id is not an object this page knows how to open (so a dead button is never shown).
 */
function ReviewButton({actionItem, index}: {actionItem: HumanActionItem; index: number}): JSX.Element | null {
  const {contextItemId} = actionItem;
  if (contextItemId == null) {
    return null;
  }

  let onClick: () => void;
  let disabledReason: string | null = null;
  if (APIUtil.isChatSession(contextItemId)) {
    onClick = () => {
      openChatInSidepanel(contextItemId);
    };
  } else if (APIUtil.isRoutine(contextItemId)) {
    onClick = () => {
      openAgentInSidepanel(contextItemId);
    };
    if (!isAgentsFeatureEnabled()) {
      disabledReason = 'Agent homepages are not available, so this agent cannot be opened from here.';
    }
  } else if (canOpenFile(contextItemId)) {
    onClick = () => {
      FileBrowserActions.openItem(contextItemId);
    };
  } else {
    return null;
  }

  return (
    <ButtonWithDisabledReason
      dataTestAttrs={{
        component: 'AgentFoundryNeedsAttention',
        element: 'reviewButton',
        index: String(index),
      }}
      variant="primary"
      size="xs"
      aria-label={`Review "${actionItem.title}"`}
      disabledTooltip={disabledReason}
      onClick={onClick}
    >
      Review
    </ButtonWithDisabledReason>
  );
}
